import React, { useState, useEffect } from 'react';
import { FileText, Download, Search, Filter, Calendar } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const PoliciesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const policiesData = [
    {
      id: 1,
      title: 'IFM Membership Guidelines 2024',
      description: 'Comprehensive guide for IFM membership application, benefits, and renewal procedures',
      category: 'Membership',
      documentType: 'PDF',
      fileSize: '2.5 MB',
      version: '2024.1',
      lastUpdated: '2024-01-15'
    },
    {
      id: 2,
      title: 'Environmental Compliance Manual',
      description: 'Complete manual for environmental compliance and MPCB regulations',
      category: 'Environmental',
      documentType: 'PDF',
      fileSize: '5.2 MB',
      version: '2024.2',
      lastUpdated: '2024-02-01'
    }
  ];

  const categories = ['all', 'membership', 'environmental', 'safety', 'labor', 'infrastructure'];
  
  const getCategoryTranslation = (category) => {
    if (category === 'all') return getText('policies.allCategories');
    const categoryMap = {
      'membership': getText('policies.category.membership'),
      'environmental': getText('policies.category.environmental'),
      'safety': getText('policies.category.safety'),
      'labor': getText('policies.category.labor'),
      'infrastructure': getText('policies.category.infrastructure'),
      // Fallback for capitalized versions
      'Membership': getText('policies.category.membership'),
      'Environmental': getText('policies.category.environmental'),
      'Safety': getText('policies.category.safety'),
      'Labor': getText('policies.category.labor'),
      'Infrastructure': getText('policies.category.infrastructure')
    };
    return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const filteredPolicies = policiesData.filter(policy => 
    (selectedCategory === 'all' || policy.category.toLowerCase() === selectedCategory) &&
    (searchTerm === '' || 
     policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     policy.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('policies.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('policies.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {categories.map(category => {
                  const categoryKey = category === 'all' ? 'all' : category;
                  return (
                    <option key={category} value={categoryKey}>
                      {getCategoryTranslation(categoryKey)}
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div className="relative">
              <Search className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder={getText('policies.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Policies Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPolicies.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{getText('policies.noPoliciesFound') || 'No policies found'}</h3>
              <p className="text-gray-500">{getText('policies.tryAdjusting') || 'Try adjusting your search or filter criteria.'}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPolicies.map((policy) => (
                <div key={policy.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                        {policy.category}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                        {policy.documentType}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{policy.title}</h3>
                    <p className="text-gray-600 mb-4">{policy.description}</p>
                    
                    <div className="space-y-2 mb-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        {getText('policies.updated') || 'Updated'}: {policy.lastUpdated}
                      </div>
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        {getText('policies.version') || 'Version'}: {policy.version}
                      </div>
                      <div className="flex items-center">
                        <Download className="h-4 w-4 mr-2" />
                        {getText('policies.size') || 'Size'}: {policy.fileSize}
                      </div>
                    </div>
                    
                    <button className="btn-primary w-full">
                      <Download className="h-4 w-4 mr-2" />
                      {getText('policies.downloadDocument') || 'Download Document'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PoliciesPage;
